// src/components/admin/business/PackageManagement.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { businessService } from '../../../services/business';
import { useNotificationCenter } from '../../../context/NotificationCenterContext';
import PackageTable from './PackageTable';
import PackageForm from './PackageForm';
import Modal from '../../common/Modal';
import Loading from '../../common/Loading';
import { FaPlus, FaSearch, FaTimes, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';

const PackageManagement = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    featured: ''
  });

  const isFirstMount = useRef(true);

  const memoizedFilters = useMemo(() => filters, [
    filters.search,
    filters.type,
    filters.status,
    filters.featured
  ]);

  const { addNotification: addToCenter } = useNotificationCenter();

  const handleApiError = useCallback(async (error, context = '') => {
    console.error(`Error ${context}:`, error);
    
    let errorMessage = '';
    if (error.message.includes('404')) {
      errorMessage = `API endpoint not found. Make sure backend server is running at http://localhost:5000`;
    } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      errorMessage = `Cannot connect to server. Make sure backend server is running.`;
    } else {
      errorMessage = `An error occurred: ${error.message}`;
    }
    
    // Show error with SweetAlert ONLY (removed toast)
    await Swal.fire({
      title: 'Error',
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'OK',
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-xl font-bold',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold'
      }
    });
  }, []);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10
      };

      Object.entries(memoizedFilters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 'undefined') {
          if (key === 'featured') {
            params[key] = value === 'true';
          } else {
            params[key] = value;
          }
        }
      });

      const response = await businessService.getAll(params);
      
      if (response?.success && response?.data) {
        const packagesData = response.data.data 
          ? Array.isArray(response.data.data) ? response.data.data : []
          : Array.isArray(response.data) ? response.data : [];
        
        setPackages(packagesData);
        
        const paginationInfo = response.data.pagination || response.pagination;
        if (paginationInfo) {
          setTotalPages(paginationInfo.pages || 1);
          setTotalItems(paginationInfo.total || packagesData.length);
        } else {
          setTotalPages(1);
          setTotalItems(packagesData.length);
        }

        isFirstMount.current = false;
      } else {
        setPackages([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      handleApiError(error, 'fetching packages');
      setPackages([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, memoizedFilters, handleApiError]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleCreate = useCallback(() => {
    setEditingPackage(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback(async (pkg) => {
    setLoadingEdit(true);
    try {
      let fullPackageData = pkg;
      
      if (pkg.id) {
        try {
          const response = await businessService.getById(pkg.id);
          if (response?.success && response?.data) {
            fullPackageData = response.data;
          }
        } catch (fetchError) {
          console.error('Error fetching full package data:', fetchError);
        }
      }
      
      setEditingPackage(fullPackageData);
      setShowForm(true);
      
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: 'Failed to load package data for editing',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    } finally {
      setLoadingEdit(false);
    }
  }, []);

  const handleDelete = useCallback(async (id, packageName) => {
    try {
      await businessService.delete(id);
      fetchPackages();
    } catch (error) {
      handleApiError(error, 'deleting package');
      throw error;
    }
  }, [fetchPackages, handleApiError]);

  const handleToggleFeatured = useCallback(async (id, currentStatus, packageName) => {
    try {
      await businessService.update(id, { featured: !currentStatus });
      fetchPackages();
    } catch (error) {
      handleApiError(error, 'toggling featured status');
      throw error;
    }
  }, [fetchPackages, handleApiError]);

  const handleStatusChange = useCallback(async (id, status, packageName) => {
    try {
      await businessService.update(id, { status });
      fetchPackages();
    } catch (error) {
      handleApiError(error, 'updating status');
      throw error;
    }
  }, [fetchPackages, handleApiError]);

  const handleFormSuccess = useCallback(async () => {
    const isEditing = editingPackage !== null;
    const packageName = editingPackage?.name || 'New Package';
    
    setShowForm(false);
    setEditingPackage(null);
    fetchPackages();

    // Show SweetAlert success popup ONLY (removed toast)
    await Swal.fire({
      title: 'Success!',
      html: isEditing 
        ? `<strong>"${packageName}"</strong> has been successfully updated.`
        : '<strong>New package</strong> has been successfully created.',
      icon: 'success',
      confirmButtonColor: '#10b981',
      confirmButtonText: 'OK',
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-xl font-bold',
        confirmButton: 'px-6 py-3 rounded-xl font-semibold'
      }
    });
  }, [fetchPackages, editingPackage]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      type: '',
      status: '',
      featured: ''
    });
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingPackage(null);
  }, []);

  const handleViewPublicPage = useCallback(() => {
    navigate('/business');
  }, [navigate]);

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  if (loading && packages.length === 0) return <Loading />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 p-8">
      <div className="max-w-9xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Business Package Management</h1>
            <p className="text-gray-600">Manage your business packages and pricing plans</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleViewPublicPage}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaEye className="mr-2 h-4 w-4" />
              View Public Page
            </button>
            
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
            >
              <FaPlus className="mr-2 h-4 w-4" />
              Add New Package
            </button>
          </div>
        </div>

        {/* Loading Edit Indicator */}
        {loadingEdit && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-sm font-medium text-blue-700">Loading package data for editing...</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 p-4">
          <div className="flex flex-wrap gap-2.5">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search packages..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all"
                />
              </div>
            </div>

            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all w-auto"
            >
              <option value="">All Types</option>
              <option value="full_management">Full Management</option>
              <option value="marketing">Marketing & Booking</option>
              <option value="operational">Operational</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all w-auto"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filters.featured}
              onChange={(e) => handleFilterChange('featured', e.target.value)}
              className="px-8 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all w-auto"
            >
              <option value="">Featured</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>

            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all"
              >
                <FaTimes className="mr-1.5 h-3 w-3" />
                Reset
              </button>
            )}
          </div>

          {activeFiltersCount > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-medium text-gray-600">{activeFiltersCount} active:</span>
                {/* Filter tags - tambahkan sesuai kebutuhan */}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <PackageTable
          packages={packages}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleFeatured={handleToggleFeatured}
          onStatusChange={handleStatusChange}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {/* Form Modal */}
        <Modal
          isOpen={showForm}
          onClose={handleCloseForm}
          title={editingPackage ? `Edit Package: ${editingPackage.name || 'Loading...'}` : 'Add New Package'}
          size="xl"
        >
          {showForm && (
            <PackageForm
              package={editingPackage}
              onSuccess={handleFormSuccess}
              onCancel={handleCloseForm}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PackageManagement;